<script>
import InfiniteLoading from 'vue-infinite-loading';
import { Fragment } from 'vue-fragment';
import Filters from '../components/Filters';
import ProductItem from '../components/ProductItem';
import PlaceholderItem from '../components/PlaceholderItem';
import VImg from '../components/VImg';
import useCollection from '../store/collection';
import SubCollection from '../components/SubCollection';

export default {
  name: 'Collection',

  components: {
    Filters,
    ProductItem,
    PlaceholderItem,
    VImg,
    InfiniteLoading,
    Fragment,
    SubCollection,
  },

  props: [
    'shop',
    'id',
    'handle',
    'title',
    'products_count',
    'sort_order',
    'template',
    'description',
    'image',
    'blocks',
  ],

  setup() {
    const { state } = useCollection();
    return { state };
  },

  data() {
    return {
      activeID: this.id,
      activeHandle: this.handle,
      activeTitle: this.title,
      productsPerPage: 10,
      currentPage: 1,
      allProductsLoaded: false,
      queryActive: false,
      label_conf: [],
    };
  },

  computed: {
    activeBlock() {
      return this.blocks.find((block) => block.collection === this.handle);
    },
    allCollections() {
      if (!this.activeBlock) return null;
      return this.activeBlock.menu?.flatMap((link) => link.links) ?? null;
    },
    subCollections() {
      if (!this.activeBlock) return null;
      return (
        this.activeBlock.menu?.find((link) => link.handle === this.activeHandle)
          ?.links ?? []
      );
    },
    numberOfPages() {
      return Math.ceil(this.state.filterProductsCount / this.productsPerPage);
    },
  },

  created() {
    this.activeHandle = globalThis.location.pathname.split('/').pop();
  },

  watch: {
    activeHandle() {
      this.switchCollection(this.activeHandle);
    },
  },

  methods: {
    switchCollection(handle) {
      const menuLink = this.activeBlock.menu.find(
        (link) => link.handle === handle
      );
      this.activeID = menuLink?.collection_id ?? this.id;
      this.activeTitle = menuLink?.title ?? this.title;
    },

    infiniteHandler: function ($state) {
      setTimeout(async function () {
        console.log('Running infiniteHandler.');
        console.log('Are all products loaded?', this.currentPage >= this.numberOfPages);

        if (this.currentPage >= this.numberOfPages) {
          $state.complete()
        } else {
          this.currentPage += 1;
          $state.loaded();
        }
      }.bind(this), 1000) // Let the initial loadCollection function run first
		},
  },

  paginationClasses: {
    li: 'pagination__part',
    liActive: 'active',
    button: 'pagination__button',
  },

  paginationLabels: {
    prev: ' ',
    next: ' ',
  },
};
</script>

<template>
  <Fragment>
    <!-- Header -->
    <div :class="bem('header')">
      <div
        v-if="!image.includes('no-image') && $screen.mobile_up"
        class="collection__header-image">
        <VImg
          :media="{ src: image }"
          :fixedSize="'1000x'"
          class="product-item__main-img" />
        <h1 class="collection__main-title">{{ title }}</h1>
      </div>
      <div class="collection__header-description">
        <div class="collection__header-description__inner">
          <h1 v-if="!$screen.mobile_up">{{ title }}</h1>
          <div v-html="description"></div>
        </div>
      </div>
    </div>

    <nav
      :class="bem('breadcrumbs') + ' page-wrapper'"
      role="navigation"
      aria-label="breadcrumbs">
      <a href="/" title="Home" v-text="$language.general.breadcrumbs.home"></a>
      <span>/ {{ title }}</span>
      <span v-if="activeTitle !== title">/ {{ activeTitle }}</span>
    </nav>

    <div
      :class="bem('container') + ' page-wrapper'"
      v-if="products_count >= 0 || template === 'search'">
      <!-- Filters -->
      <div :class="bem('filters')">
        <template v-if="activeBlock">
          <h3 :class="bem('filters-title')" v-if="$screen.tablet_m_up">
            {{ activeBlock.title }}
          </h3>
          <ul :class="bem('menu-links')">
            <li
              v-for="(link, idx) in activeBlock.menu"
              v-text="link.title"
              @click="activeHandle = link.handle"
              :class="{ active: activeHandle === link.handle }"
              :key="idx"></li>
          </ul>
        </template>
        <Filters
          :shop="shop"
          :id="activeID"
          :handle="activeHandle"
          :productsPerPage="productsPerPage"
          :page="currentPage"
          :sort_order="sort_order"
          :activeBlock="activeBlock"
          @resetPage="currentPage = 1"
          @updatePage="allProductsLoaded = productsPerPage < $event;" />
      </div>

      <!-- Products -->
      <div
        v-if="state.filtersActive || !this.activeBlock || !this.allCollections"
        :class="bem('grid')">
        <ProductItem
          v-for="product in state.products"
          :product="product"
          :collection="handle"
          :label_conf="label_conf"
          :key="product.id" />
      </div>
      <div v-else :class="[bem('grid'), 'sub-collections']">
        <div v-for="collection in allCollections" :key="collection">
          <keep-alive>
            <SubCollection
              v-if="
                subCollections.includes(collection) || activeHandle === handle
              "
              :handle="collection" />
          </keep-alive>
        </div>
      </div>

      <!-- Pagination -->
      <infinite-loading
        class="collection-products-infinite-loading"
        :identifier="state.infiniteId"
        @infinite="infiniteHandler">
        <div slot="no-more"></div>
        <div slot="no-results"></div>
        <div slot="spinner">
          <div class="site-loading active">
            <div class="spinner">
              <div class="circle"></div>
            </div>
          </div>
        </div>
      </infinite-loading>
    </div>

    <div v-else class="no-results">
      <h4>Sorry, no products found</h4>
    </div>
  </Fragment>
</template>
